import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, type FormEvent } from 'react'

export interface AddressFormState {
  name: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zip: string
}

export interface AddressFormDialogProps {
  isOpen: boolean
  mode: 'create' | 'edit'
  onClose: () => void
  formState: AddressFormState
  onFieldChange: (field: keyof AddressFormState, value: string) => void
  selectedFile: File | null
  onFileChange: (file: File | null) => void
  // The address's current cover image (server-managed URL), shown as the preview
  // when editing and no new file has been picked. Uploading a file replaces it.
  existingLogoUrl?: string | null
  // Edit-only: when true, the existing image is cleared on save (sent as the
  // `removeImage` flag). Mutually exclusive with selecting a new file.
  removeImage: boolean
  onRemoveImageChange: (value: boolean) => void
  onSubmit: () => void
  isSubmitting: boolean
  error?: string | null
}

const inputClassName =
  'mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600'

const labelClassName = 'block text-sm font-medium text-gray-900'

const AddressFormDialog = ({
  isOpen,
  mode,
  onClose,
  formState,
  onFieldChange,
  selectedFile,
  onFileChange,
  existingLogoUrl,
  removeImage,
  onRemoveImageChange,
  onSubmit,
  isSubmitting,
  error,
}: AddressFormDialogProps) => {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit()
  }

  // Preview the picked file via an object URL; fall back to the existing
  // cover image. The newly selected file wins because, on save, the uploaded
  // file replaces the stored image on the backend.
  const filePreviewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  )

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl)
      }
    }
  }, [filePreviewUrl])

  // A pending removal clears the preview; a newly picked file always wins.
  const previewUrl =
    filePreviewUrl ?? (removeImage ? null : existingLogoUrl?.trim() || null)

  const hasExistingImage = Boolean(existingLogoUrl?.trim())
  const canRemoveExisting =
    mode === 'edit' && hasExistingImage && !selectedFile

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-5 py-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
            <div className="flex items-start justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-gray-900">
                {mode === 'edit' ? 'Edit address' : 'Add an address'}
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="-m-2 rounded-md p-2 text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Save a meeting location so you can attach it to a recurring
              meeting.
            </p>

            {previewUrl ? (
              <div className="mt-4 overflow-hidden rounded-lg ring-1 ring-gray-200">
                <img
                  src={previewUrl}
                  alt="Meeting cover preview"
                  className="aspect-[16/9] w-full object-cover"
                />
              </div>
            ) : null}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="address-name" className={labelClassName}>
                  Location name
                </label>
                <input
                  id="address-name"
                  name="name"
                  type="text"
                  value={formState.name}
                  onChange={(event) => onFieldChange('name', event.target.value)}
                  placeholder="e.g. Downtown Library"
                  className={inputClassName}
                  required
                />
              </div>

              <div>
                <label htmlFor="address-line1" className={labelClassName}>
                  Address line 1
                </label>
                <input
                  id="address-line1"
                  name="addressLine1"
                  type="text"
                  value={formState.addressLine1}
                  onChange={(event) =>
                    onFieldChange('addressLine1', event.target.value)
                  }
                  placeholder="Street address"
                  className={inputClassName}
                  autoComplete="address-line1"
                  required
                />
              </div>

              <div>
                <label htmlFor="address-line2" className={labelClassName}>
                  Address line 2{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  id="address-line2"
                  name="addressLine2"
                  type="text"
                  value={formState.addressLine2}
                  onChange={(event) =>
                    onFieldChange('addressLine2', event.target.value)
                  }
                  placeholder="Apartment, suite, unit, etc."
                  className={inputClassName}
                  autoComplete="address-line2"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="address-city" className={labelClassName}>
                    City
                  </label>
                  <input
                    id="address-city"
                    name="city"
                    type="text"
                    value={formState.city}
                    onChange={(event) =>
                      onFieldChange('city', event.target.value)
                    }
                    className={inputClassName}
                    autoComplete="address-level2"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="address-state" className={labelClassName}>
                    State
                  </label>
                  <input
                    id="address-state"
                    name="state"
                    type="text"
                    value={formState.state}
                    onChange={(event) =>
                      onFieldChange('state', event.target.value)
                    }
                    className={inputClassName}
                    autoComplete="address-level1"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="address-zip" className={labelClassName}>
                    ZIP
                  </label>
                  <input
                    id="address-zip"
                    name="zip"
                    type="text"
                    value={formState.zip}
                    onChange={(event) =>
                      onFieldChange('zip', event.target.value)
                    }
                    className={inputClassName}
                    autoComplete="postal-code"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="address-image" className={labelClassName}>
                  Cover image{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <input
                    id="address-image"
                    name="file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) =>
                      onFileChange(event.target.files?.[0] ?? null)
                    }
                    className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  {selectedFile && (
                    <button
                      type="button"
                      onClick={() => onFileChange(null)}
                      className="text-sm font-medium text-gray-500 hover:text-gray-700">
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  JPEG, PNG, or WebP up to 5&nbsp;MB. Used as the meeting cover
                  photo on the public meeting card. Uploading a new image
                  replaces the current one.
                </p>
                {canRemoveExisting &&
                  (removeImage ? (
                    <p className="mt-2 text-xs text-gray-500">
                      Current image will be removed when you save.{' '}
                      <button
                        type="button"
                        onClick={() => onRemoveImageChange(false)}
                        className="font-medium text-indigo-600 hover:text-indigo-500">
                        Undo
                      </button>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRemoveImageChange(true)}
                      className="mt-2 text-sm font-medium text-red-600 hover:text-red-800">
                      Remove current image
                    </button>
                  ))}
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  {isSubmitting ? 'Saving...' : 'Save address'}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default AddressFormDialog
